import React, { memo, FC, useEffect, useState, useRef, MutableRefObject, Ref, forwardRef, useImperativeHandle, useContext } from 'react'
import FormContainer, { FormContainerSubmitHandler, FormContainerChild } from '../../../../legacy-components/form/container/FormContainer';
import { Field, useFormState } from 'react-final-form';
import FormSelectionGrid, { FormSelectionGridRef } from '../../../../legacy-components/form/input/FormSelectionGrid';
import { AccordionContent } from '../../../../legacy-components/form/components/accordion-form/AccordionForm';
import ProductService, { IAPIProduct } from '../../../../libs/resources/product/ProductService';
import { useAsyncAction, AsyncLoader } from '../../../../legacy-components/form/action/AsyncAction';
import Skeleton from '@material-ui/lab/Skeleton';
import { Box, Divider } from '@material-ui/core';
import { AssignableCollectionItem } from '../../../../legacy-components/form/collection/AssignableCollection';
import { ProductProxyFormData, ProductSubscriptionFormData } from '../models/products.model';
import { OnChange } from 'react-final-form-listeners';
import UnifiedAPIService, { UnifiedAPI } from '../../../../services/UnifiedAPIService';
import { FORM_ERROR } from 'final-form';
import { useFeatureAbility, useResourceAbility } from 'libs/security/authorization/Permission';
import { useConfirm } from 'material-ui-confirm';
import { SnackNotificationContext } from '../../../../legacy-components/notifications/GenericSnackNotification';
import { ProductSubscriptionsControls, ProductSubscriptionTitle, useProductSubscriptionGrid } from '../product-forms/ProductSubscriptionInfo';
import { useHistory } from 'react-router';
import SubscriptionService, { IAPIProductSubscription } from 'libs/resources/subscription/SubscriptionService';
import { ExportAsCSV } from '../ExportAsCSV';
import Alert from '@material-ui/lab/Alert';
import { Feature } from 'libs/security/authorization/FeatureAbility';

interface ProductProxyViewProps {
    product: IAPIProduct
}

interface ProxyPickerFormProps {
    product: IAPIProduct;
}

interface SubscriptionPickerFormRef {
    formProxyFieldMount: boolean
}

interface ProxyPickerGridRef {
    revertChanges(): void;
    commitChanges(): void;
}

interface SubscriptionPickerGridProps {
    productName: string,
    proxyFormRef: MutableRefObject<SubscriptionPickerFormRef>,
    onSubmit: () => void,
    ref: Ref<ProxyPickerGridRef | undefined>,
    readOnly?: boolean
}

const ProxyGridSkeleton = () => {
    const [values] = useState(Array.from(Array(5).keys()));

    return (
        <>
            <Divider />
            <Box padding="24px" width="100%">
                <Box display="flex" justifyContent="space-between" width="100%">
                    <Skeleton width={160} />
                    <Skeleton variant="circle" width={32} height={32} />
                </Box>
                <Box marginTop="16px" width="100%" height="100%" padding="24px">
                    <Box display="flex" justifyContent="space-between" width="100%" height="40px">
                        <Skeleton height={20} width={80} />
                        <Box display="flex" width={140} justifyContent="space-between">
                            <Skeleton height={20} width={100} />
                            <Box height={20} width={80} />
                        </Box>
                    </Box>
                    <Divider />
                    <Box width="100%" height="180px">
                        {
                            values.map(value => (
                                <Box key={value} display="flex" marginTop="16px" justifyContent="space-between" width="100%" height="24px">
                                    <Skeleton height={20} width={160} />
                                    <Box display="flex" width={140} justifyContent="space-between">
                                        <Skeleton height={20} width={80} />
                                        <Skeleton variant="circle" height={20} width={20} />
                                    </Box>
                                </Box>
                            ))
                        }
                    </Box>
                </Box>
            </Box>
        </>
    )
}

const SubscriptionPickerGrid: FC<SubscriptionPickerGridProps> = forwardRef(({ productName, proxyFormRef, onSubmit, readOnly }, ref) => {
    const formState = useFormState<ProductSubscriptionFormData>();
    const fieldRef = useRef<FormSelectionGridRef>();

    const { gridData, gridOptions, onChange, disabled } = useProductSubscriptionGrid(productName, {
        headerStyle: "none"
    });

   // const handleOpenDialog = useCallback(() => openDialog(() => formState.values?.subscriptions?.list), [formState, openDialog]);
    let history = useHistory();
    const viewSubscriptions = (productName: string) => {
        history.push(`/home/products/${productName}/subscriptions`)
    }
    const exportSubscriptions = () => {
        ProductService.getSubscriptions(productName, "detailed")
        .toPromise()
        .then(response => {
            response.forEach(subscription => {
                if(!subscription.status)
                    subscription.status = "SUBSCRIBED"
            })
            ExportAsCSV(response, "file")
        })
    }
    const ContentHeader = (<ProductSubscriptionTitle count={formState.values["subscriptions"]?.count ?? formState.values?.subscriptions?.list?.length} />);
    const ContentControls = (<ProductSubscriptionsControls disabled={disabled} 
        onViewClick={ ()=>viewSubscriptions(productName)} onExportClick = {()=>exportSubscriptions()} />
    );

    useImperativeHandle(ref, () => ({
        revertChanges: () => fieldRef.current?.revertChanges(),
        commitChanges: () => fieldRef.current?.commitChanges()
    }), [fieldRef]);

    useEffect(() => {
        if (proxyFormRef.current.formProxyFieldMount) {
            if (formState.values?.subscriptions && Array.isArray(formState.values?.subscriptions?.list)) {
                onChange(formState.values.subscriptions.list
                    .map((item: AssignableCollectionItem<IAPIProductSubscription>) => ({
                        key: item.value.applicationName + "-" + item.value.gatewayName,
                        value: item.value,
                        assign: true,
                        assigned: false
                    })));
            }
            proxyFormRef.current.formProxyFieldMount = false;
        }
    }, [proxyFormRef, formState, onChange]);

    return (
        <AccordionContent header={ContentHeader} controls={ContentControls}>
            <br/>
            <Field
                name="subscriptions"
                component={FormSelectionGrid}
                gridOptions={gridOptions}
                gridData={gridData}
                ref={fieldRef}
                readOnly={true}
            />
            <OnChange name="subscriptions">
                {() => onSubmit()}
            </OnChange>
        </AccordionContent>
    )
});

const createSubscriptionCollection = (subscriptions: IAPIProductSubscription[]) => {
    return subscriptions?.map(subscription => {
        return ({
            key: subscription.subscriptionId,
            value: subscription,
            assign: true,
            assigned: true
        });
    }) ?? [];
}

const SubscriptionPickerForm = ({ product }: ProxyPickerFormProps) => {
    const proxyFormRef = useRef({
        formProxyFieldMount: true
    });
    const confirm = useConfirm();
    const proxyGridRef = useRef<ProxyPickerGridRef>();

    const [response, { subscribe }] = useAsyncAction(() => ProductService.getSubscriptions(product.name, "recent"),
        {
            mapFn: createSubscriptionCollection
        });

    useEffect(() => {
        const unsubscribe = subscribe()
        return () => unsubscribe();
    }, [subscribe]);


    const NotificationMessage = (props: any) => {
        return (
            <span>{props.error}</span>
        )
    }
    const notificationContext = useContext(SnackNotificationContext);
    const onSubmit: FormContainerSubmitHandler<ProductProxyFormData> = () => (data) => {
        return new Promise((resolve) => {
            if (data.proxies?.hasChanges) {
                if (data.proxies?.toRemove.length > 0) {
                    confirm({ description: 'Do you want to unmap this proxy permanently?', dialogProps: { open: true, disableBackdropClick: true } })
                        .then(() => {
                            UnifiedAPIService.post(
                                UnifiedAPI.parseRequestURL(UnifiedAPI.map_proxy_product, {
                                    productName: product.name
                                }), {
                                addApis: data.proxies?.toAdd.map(proxy => proxy.value.name).filter(value => !!value),
                                removeApis: data.proxies?.toRemove.map(proxy => proxy.value.name).filter(value => !!value)
                            }).then(() => {
                                proxyGridRef.current?.commitChanges();
                                resolve()
                            }, error => {
                                proxyGridRef.current?.revertChanges();
                                resolve({
                                    [FORM_ERROR]: error.response?.data?.message ?? "Unknown error occured while selecting the proxy"
                                });
                            });
                        }).catch(() => {
                            proxyGridRef.current?.revertChanges();
                            resolve();
                        });
                } else {
                    UnifiedAPIService.post(
                        UnifiedAPI.parseRequestURL(UnifiedAPI.map_proxy_product, {
                            productName: product.name
                        }), {
                        addApis: data.proxies?.toAdd.map(proxy => proxy.value.name).filter(value => !!value),
                        removeApis: data.proxies?.toRemove.map(proxy => proxy.value.name).filter(value => !!value)
                    }).then(() => {
                        proxyGridRef.current?.commitChanges();
                        resolve()
                    }, error => {
                        proxyGridRef.current?.revertChanges();
                        if (error.response?.data?.message) {
                            notificationContext.show({
                                timeout: 6000,
                                id: "not-verified",
                                message: <NotificationMessage error={error.response?.data?.message} />
                            });
                        }
                        resolve({
                            [FORM_ERROR]: error.response?.data?.message ?? "Unknown error occured while selecting the proxy"
                        });
                    });
                }
            }
            resolve();
        })
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [can,, ability] = useResourceAbility({
        resource: ({ Product }) => Product.mapping
    });

    const FormComponents: FormContainerChild<ProductSubscriptionFormData> = (formProps, _options, triggerSubmit) => {
        const onSubmit = useRef(() => triggerSubmit());
        return (<SubscriptionPickerGrid ref={proxyGridRef} productName={product.name} proxyFormRef={proxyFormRef} onSubmit={onSubmit.current} 
            readOnly={!can.create()} />);
    }

    return (
        <AsyncLoader state={response} loader={<ProxyGridSkeleton />}>
            {response.value && ability.valid && <FormContainer previewErrors={true} FormProps={{
                onSubmit, initialValues: {
                    subscriptions: {
                        list: response.value
                    }
                }
            }} children={FormComponents} />}
        </AsyncLoader>
    );
}

function ProductSubscriptionView({ product }: ProductProxyViewProps) {
    return (
        <SubscriptionPickerForm product={product} />
    )
}

export default memo(ProductSubscriptionView);
